/**
 * Attendance Firestore API
 *
 * Provides real-time subscriptions and write operations for attendance records
 * stored in the scoped project `attendance/{eventId}` subcollection.
 */
import { db } from '../../firebase-config';
import {
  collection,
  type CollectionReference,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { AttendanceRecord, ActorAttendance } from './types';
import { logger } from '@/utils/logger';
import { buildProjectSubcollectionPath, type ProjectScope } from '../sync/project-scope';
import { createAndStoreProjectWriteIntent } from '../sync/project-write-operation-factory';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';
import {
  markPendingAttendanceActorWrite,
  markPendingAttendanceRecordWrite,
} from '../sync/attendance-pending-writes';
import { getEffectiveUserId } from '../auth/current-user-identity';

type AttendanceRecordDoc = AttendanceRecord;

/**
 * Subscribe to all attendance records for a project.
 * Returns an unsubscribe function.
 */
export function subscribeToAttendance(
  scope: ProjectScope,
  callback: (records: AttendanceRecord[]) => void
): Unsubscribe {
  const colRef = collection(
    db,
    buildProjectSubcollectionPath(scope, 'attendance')
  ) as CollectionReference<AttendanceRecordDoc>;

  return onSnapshot(
    colRef,
    (snapshot) => {
      const records: AttendanceRecord[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const coverCredits = Array.isArray(data.coverCredits) ? data.coverCredits : undefined;
        return {
          eventId: docSnap.id,
          eventDate: data.eventDate ?? '',
          eventTitle: data.eventTitle ?? '',
          eventStartTime: data.eventStartTime ?? '',
          gracePeriodMinutes: data.gracePeriodMinutes ?? 5,
          actors: data.actors ?? {},
          ...(coverCredits ? { coverCredits } : {}),
        };
      });
      callback(records);
    },
    (error: unknown) => {
      if (error instanceof Error) {
        logger.error('[attendance] Snapshot error:', error.message);
      }
    }
  );
}

export function buildConflictExcusedActors(
  existingActors: Record<string, ActorAttendance> = {},
  conflictedActorIds: string[] = []
): Record<string, ActorAttendance> {
  const excusedActors: Record<string, ActorAttendance> = {};
  const uniqueIds = new Set(conflictedActorIds.filter(Boolean));

  for (const actorId of uniqueIds) {
    if (!existingActors[actorId]) {
      excusedActors[actorId] = { status: 'excused' };
    }
  }

  return excusedActors;
}

/**
 * Ensure an attendance record exists for an event.
 * Preserves the write through the project-write outbox. The server resolver
 * merges conflict-derived excused statuses without overwriting actors who have
 * already signed in or been manually adjusted.
 */
export async function ensureAttendanceRecord(
  scope: ProjectScope,
  eventId: string,
  eventDate: string,
  eventTitle: string,
  eventStartTime: string,
  gracePeriodMinutes: number = 5,
  conflictedActorIds: string[] = []
): Promise<void> {
  await enqueueAttendanceWrite({
    scope,
    conflictUnit: `attendance/${eventId}`,
    payload: sanitizeDocumentForFirestore({
      eventId,
      eventDate,
      eventTitle,
      eventStartTime,
      gracePeriodMinutes,
      actors: buildConflictExcusedActors({}, conflictedActorIds),
    }),
  });
}

/**
 * Write an actor's attendance status (SM override).
 * Updates only the specific actor's data within the attendance doc.
 */
export async function writeActorStatus(
  scope: ProjectScope,
  eventId: string,
  actorId: string,
  attendance: ActorAttendance
): Promise<void> {
  const writeActorId = requireAttendanceActorId();
  markPendingAttendanceActorWrite(scope, eventId, actorId);
  await enqueueAttendanceWrite({
    scope,
    actorId: writeActorId,
    conflictUnit: `attendance/${eventId}/actors/${actorId}`,
    payload: sanitizeDocumentForFirestore({
      eventId,
      actorId,
      attendance,
    }),
  });
}

export async function writeAttendanceCoverCredits(
  scope: ProjectScope,
  record: AttendanceRecord
): Promise<void> {
  const writeActorId = requireAttendanceActorId();
  markPendingAttendanceRecordWrite(scope, record.eventId);
  await enqueueAttendanceWrite({
    scope,
    actorId: writeActorId,
    conflictUnit: `attendance/${record.eventId}`,
    payload: sanitizeDocumentForFirestore({
      eventId: record.eventId,
      eventDate: record.eventDate,
      eventTitle: record.eventTitle,
      eventStartTime: record.eventStartTime,
      gracePeriodMinutes: record.gracePeriodMinutes,
      actors: record.actors ?? {},
      coverCredits: record.coverCredits ?? [],
    }),
  });
}

function requireAttendanceActorId(): string {
  const actorId = getEffectiveUserId();
  if (!actorId) {
    throw new Error('Authentication required to preserve attendance project writes');
  }
  return actorId;
}

async function enqueueAttendanceWrite(input: {
  scope: ProjectScope;
  actorId?: string;
  conflictUnit: string;
  payload: unknown;
}): Promise<void> {
  await createAndStoreProjectWriteIntent({
    scope: input.scope,
    actorId: input.actorId ?? requireAttendanceActorId(),
    feature: 'attendance',
    capability: 'EDIT_RUNSHEET',
    kind: 'patchEntity',
    conflictUnit: input.conflictUnit,
    payload: input.payload,
  });
}
